import { useCallback, useMemo } from "react";
import DOMPurify from "dompurify";

const HOW_IT_WORKS_BULLET_PREFIX = "how_it_works_bullet_";
const IMPORTANT_INFO_BULLET_PREFIX = "important_info_bullet_";

export interface UseHtsAirCfarHowItWorksDialogParams {
  merchandisingContents: Record<string, string>;
}

export interface UseHtsAirCfarHowItWorksDialogResult {
  title: string;
  subtitle: string;
  disclaimer: string;
  termsAndConditions: string;
  howItWorksBullets: string[];
  importantInfoHeader: string;
  importantInfoBullets: string[];
  importantInfoRedemptionRate: string;
}

/**
 * Parses merchandisingContents for the How It Works dialog.
 *
 * Extracts `how_it_works_title`, `how_it_works_subtitle` (falling back to
 * `how_it_works_subtitle_no_loyalty` when absent — processMerchandisingTemplates
 * omits the loyalty key when the user has no rewards account),
 * `how_it_works_bullet_disclaimer`,
 * `how_it_works_terms_and_conditions`,
 * numerically-sorted `how_it_works_bullet_N` entries,
 * `important_info_header`, and numerically-sorted `important_info_bullet_N` entries.
 * `important_info_redemption_rate` is available for some card types.
 * All HTML content is sanitized via DOMPurify before rendering.
 */
export const useHtsAirCfarHowItWorksDialog = ({
  merchandisingContents,
}: UseHtsAirCfarHowItWorksDialogParams): UseHtsAirCfarHowItWorksDialogResult => {
  const getBullets = useCallback(
    (bulletPrefix: string) => {
      const pattern = new RegExp(`^${bulletPrefix}\\d+$`);
      return Object.entries(merchandisingContents)
        .filter(([key]) => pattern.test(key))
        .sort(([a], [b]) => {
          const numA = parseInt(a.replace(bulletPrefix, ""), 10);
          const numB = parseInt(b.replace(bulletPrefix, ""), 10);
          return numA - numB;
        })
        .map(([, value]) => DOMPurify.sanitize(value))
        .filter((value) => value !== "");
    },
    [merchandisingContents]
  );

  const title = merchandisingContents.how_it_works_title ?? "";
  const subtitle = DOMPurify.sanitize(
    merchandisingContents.how_it_works_subtitle ??
      merchandisingContents.how_it_works_subtitle_no_loyalty ??
      ""
  );
  const disclaimer = DOMPurify.sanitize(
    merchandisingContents.how_it_works_bullet_disclaimer ?? ""
  );

  const termsAndConditions = DOMPurify.sanitize(
    merchandisingContents.how_it_works_terms_and_conditions ?? ""
  );

  const howItWorksBullets = useMemo(
    () => getBullets(HOW_IT_WORKS_BULLET_PREFIX),
    [getBullets]
  );

  const importantInfoHeader = DOMPurify.sanitize(
    merchandisingContents.important_info_header ?? ""
  );
  const importantInfoRedemptionRate = DOMPurify.sanitize(
    merchandisingContents.important_info_redemption_rate ?? ""
  );

  const importantInfoBullets = useMemo(
    () => getBullets(IMPORTANT_INFO_BULLET_PREFIX),
    [getBullets]
  );

  return {
    title,
    subtitle,
    disclaimer,
    termsAndConditions,
    howItWorksBullets,
    importantInfoHeader,
    importantInfoBullets,
    importantInfoRedemptionRate,
  };
};
